import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useAuth } from './AuthContext';

interface PointsAnimation {
  id: number;
  amount: number;
  startX: number;
  startY: number;
}

interface PointsContextType {
  points: number;
  addPoints: (amount: number, sourceElement: HTMLElement) => void;
  targetRef: React.RefObject<HTMLDivElement | null>;
  animations: PointsAnimation[];
  removeAnimation: (id: number) => void;
  triggerBump: boolean;
  triggerImpact: boolean;
}

const PointsContext = createContext<PointsContextType | undefined>(undefined);

export const usePoints = () => {
  const context = useContext(PointsContext);
  if (!context) {
    throw new Error('usePoints must be used within a PointsProvider');
  }
  return context;
};

let nextAnimationId = 0;

export const PointsProvider = ({ children }: { children: ReactNode }) => {
  const { user, isAuthenticated } = useAuth();
  const [points, setPoints] = useState(0);
  const [animations, setAnimations] = useState<PointsAnimation[]>([]);
  const [triggerBump, setTriggerBump] = useState(false);
  const [triggerImpact, setTriggerImpact] = useState(false);
  const targetRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setPoints(isAuthenticated && user ? user.points : 0);
  }, [isAuthenticated, user]);

  const addPoints = useCallback((amount: number, sourceElement: HTMLElement) => {
    const rect = sourceElement.getBoundingClientRect();
    const id = nextAnimationId++;

    setAnimations(prev => [...prev, {
      id,
      amount,
      startX: rect.left + rect.width / 2,
      startY: rect.top + rect.height / 2,
    }]);

    setTimeout(() => {
      setPoints(prev => prev + amount);
      setTriggerBump(true);
      setTriggerImpact(true);
      setTimeout(() => setTriggerBump(false), 600);
      setTimeout(() => setTriggerImpact(false), 800);
    }, 750);
  }, []);

  const removeAnimation = useCallback((id: number) => {
    setAnimations(prev => prev.filter(a => a.id !== id));
  }, []);

  return (
    <PointsContext.Provider value={{ points, addPoints, targetRef, animations, removeAnimation, triggerBump, triggerImpact }}>
      {children}
    </PointsContext.Provider>
  );
};
